import React, { memo } from 'react';
import { Stack, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { StyledParams } from 'types/common';
import { Lead } from 'types/user';
import { partition } from 'lodash';
import { renderTitleLead } from 'utils/renderTitleLead';
import { FileNote } from 'services/fileNote';
type SummaryLogProps = {
  leads: Partial<Lead>[];
  noTitle?: boolean;
  fileNotes?: FileNote[];
};
export const SummaryLogComponent = ({ leads = [], noTitle, fileNotes = [] }: SummaryLogProps) => {
  return (
    <WrapperNoBorder>
      {!noTitle && (
        <WrapperHeaderBox>
          <TitleLead>Notes</TitleLead>
        </WrapperHeaderBox>
      )}
      <WrapperContentNote flex={1}>
        <TextNote>Summary Log</TextNote>
        <Stack gap={1.5}>
          {!!fileNotes?.length && <Typography sx={{ fontWeight: 700 }}>File Notes</Typography>}
          {fileNotes?.map((itemNote, index) => {
            return (
              <WrapperNoteContent key={index}>
                <Stack key={index} gap={0.5} flexDirection={'row'}>
                  <TextDate>{`${dayjs(itemNote.createdAt).format('M/D/YYYY, h:mm A')}`}</TextDate>
                  <Stack
                    width={'100%'}
                    flexDirection={'row'}
                    gap={0.5}
                    alignItems={'flex-start'}
                    justifyContent={'space-between'}>
                    <Stack width={'100%'} gap={0.7}>
                      <TextNote>{itemNote?.content}</TextNote>
                    </Stack>
                  </Stack>
                </Stack>
              </WrapperNoteContent>
            );
          })}
          {leads?.map((item, index) => {
            const [noteAdmin, noteBailiff] = partition(
              item.notes,
              (note) => note.user.role === 'ADMIN' || note.user.role === 'ROOT_ADMIN'
            );
            if (!item.notes?.length) return;
            return (
              <WrapperNoteContent key={index}>
                <Typography sx={{ fontWeight: 700 }}>{renderTitleLead(item)}</Typography>
                {noteBailiff?.length !== 0 && (
                  <Stack gap={1}>
                    {noteBailiff?.map((note, index) => {
                      return (
                        <Stack key={index} gap={0.5} flexDirection={'row'}>
                          <TextDate>
                            {`${dayjs(note.createdAt).format('M/D/YYYY, h:mm A')}
                        -`}
                          </TextDate>
                          <Stack
                            width={'100%'}
                            flexDirection={'row'}
                            gap={0.5}
                            alignItems={'flex-start'}
                            justifyContent={'space-between'}>
                            <Stack width={'100%'} gap={0.7}>
                              <TextNote>
                                {note.content} -
                                <span style={{ color: '#000', fontWeight: 600 }}>
                                  {note.user.firstName} {note.user.lastName}
                                </span>
                              </TextNote>
                            </Stack>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
                {noteAdmin?.length !== 0 && (
                  <Stack gap={1}>
                    <TextNote>Admin Note:</TextNote>
                    {noteAdmin?.map((item, index) => {
                      const displayName =
                        item?.user?.role === 'ROOT_ADMIN'
                          ? 'Master Admin'
                          : item?.user?.firstName || item?.user?.lastName
                            ? `${item?.user?.firstName ?? ''} ${item?.user?.lastName ?? ''}`.trim()
                            : 'Admin';
                      return (
                        <Stack gap={0.5} flexDirection={'row'} key={index}>
                          <TextDate>
                            {`${dayjs(item.createdAt).format('M/D/YYYY, h:mm A')}`}
                          </TextDate>
                          <TextNote key={index}>
                            {item?.content} -
                            <span style={{ color: '#000', fontWeight: 600 }}>{displayName}</span>
                          </TextNote>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </WrapperNoteContent>
            );
          })}
        </Stack>
      </WrapperContentNote>
    </WrapperNoBorder>
  );
};
export const SummaryLog = memo(SummaryLogComponent);
const WrapperNoteContent = styled(Stack)(() => ({
  gap: '10px'
}));
const WrapperContentNote = styled(Stack)(({ theme }: StyledParams) => ({
  background: theme.colors.blue,
  borderRadius: '5px',
  width: '100%',
  padding: '8px 10px',
  gap: '10px'
}));
const TextNote = styled(Typography)(() => ({
  color: '#565656',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'pre-line'
}));

const TextDate = styled(Typography)(() => ({
  color: '#ADADAD',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '100%',
  minWidth: '145px'
}));
const WrapperNoBorder = styled(Stack)(() => ({
  paddingBottom: '16px',
  height: '100%'
}));
const WrapperHeaderBox = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '10px'
}));
const TitleLead = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.light1,
  fontSize: 18,
  fontWeight: 500,
  lineHeight: '100%'
}));
